"use client"

import { useEffect, useState } from "react"
import { getAuth as getSecondaryAuth, createUserWithEmailAndPassword, deleteUser } from "firebase/auth"
import { ref, update, get, remove } from "firebase/database"
import { auth, db, firebaseConfig } from "../Firebase/firebase-config"
import { initializeApp } from "firebase/app"

import "./partnerStyle.css"

import PartnerNavbar from "./partner-navbar"
import ProfileDialog from "./profile-dialog"
import { FaInfoCircle } from "react-icons/fa"

export default function PartnerManagement() {
  const [partnerData, setPartnerData] = useState({ email: "", password: "", name: "" })
  const [message, setMessage] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [partners, setPartners] = useState([])
  const [partnerToRemove, setPartnerToRemove] = useState(null) // uid of partner to remove

  const [mainProfile, setMainProfile] = useState(null)
  const [profileOpen, setProfileOpen] = useState(false)

  // Secondary app for creating partner without logging out main user
  const secondaryApp = initializeApp(firebaseConfig, "Secondary")
  const secondaryAuth = getSecondaryAuth(secondaryApp)


  // Fetch main user's profile
  useEffect(() => {
    const fetchMainProfile = async () => {
      try {
        const user = auth?.currentUser
        if (!user) return
        const mainSnap = await get(ref(db, `profileDashboard/${user.uid}`))
        if (mainSnap.exists()) {
          setMainProfile(mainSnap.val())
        }
      } catch (err) {
        console.log("[v0] Failed to fetch main profile:", err?.message)
      }
    }
    fetchMainProfile()
  }, [])

  // Fetch partners
  const fetchPartners = async () => {
    try {
      const user = auth?.currentUser
      if (!user) return
      const partnersSnap = await get(ref(db, `profileDashboard/${user.uid}/partners`))
      if (partnersSnap.exists()) {
        const partnersData = Object.entries(partnersSnap.val()).map(([uid, data]) => ({
          uid,
          ...data
        }))
        setPartners(partnersData)
      } else {
        setPartners([])
      }
    } catch (err) {
      console.log("Failed to fetch partners:", err)
    }
  }

  useEffect(() => {
    fetchPartners()
  }, [])

  const handleChange = (e) => {
    setPartnerData({ ...partnerData, [e.target.name]: e.target.value })
  }

  const handleAddPartner = async (e) => {
    e?.preventDefault()
    setMessage("")
     if (!partnerData.name || !partnerData.email || !partnerData.password) {
    setMessage("Error: Please fill all required fields.");
    setTimeout(()=>{
      setMessage("")
    },2000)
    return;
  }

  setSubmitting(true);
    try {
      const mainUser = auth.currentUser
      if (!mainUser) throw new Error("Main user not logged in.")

      // 1️⃣ Fetch main user's profile
      const mainProfileSnap = await get(ref(db, `profileDashboard/${mainUser.uid}`))
      const mainProfileLocal = mainProfileSnap.exists() ? mainProfileSnap.val() : {}

      // 2️⃣ Create partner using secondary auth
      const partnerUser = await createUserWithEmailAndPassword(
        secondaryAuth,
        partnerData.email,
        partnerData.password
      )
      const partnerUid = partnerUser.user.uid

      // 3️⃣ Save partner info under main user's profile
      const partnerRef = ref(db, `profileDashboard/${mainUser.uid}/partners/${partnerUid}`)
      await update(partnerRef, {
        email: partnerData.email,
        name: partnerData.name,
        createdAt: Date.now(),
        mainUserId: mainUser.uid,
        mainUserGST: mainProfileLocal.gstin || "",
        mainUserPAN: mainProfileLocal.pan || "",
        mainUserCompany: mainProfileLocal.company_name || ""
      })

      setMessage("Partner added successfully!")
      setPartnerData({ email: "", password: "", name: "" })
      fetchPartners() // refresh partners list
      setTimeout(()=>{
      setMessage("")
      },2000)
    } catch (error) {
  if (error.code === "auth/email-already-in-use") {
    setMessage("This user already exist. Please try with a different email.")
  } else {
    setMessage("Error: " + (error?.message || "Unable to add partner"))
  }
} finally {
      setSubmitting(false)
    }
  }
  // Remove partner
  const handleRemovePartner = async (partnerUid) => {


    try {
      const mainUser = auth.currentUser
      if (!mainUser) throw new Error("Main user not logged in.")

      // Remove partner from database
      await remove(ref(db, `profileDashboard/${mainUser.uid}/partners/${partnerUid}`))

      setMessage("Partner removed successfully!")
      fetchPartners() // refresh partners list
    } catch (error) {
      setMessage("Error removing partner: " + (error?.message || "Unknown error"))
    }
  }

  
 // Function to show info
const showPartnerInfo = () => {
  alert(
    `Here’s how adding a partner works:\n\n` +
    `1. Enter the partner's Name, Email, and Password.\n` +
    `2. When you add a partner, their account is created under your profile.\n` +
    `3. Partners get basic info like Company Name, GSTIN, and PAN from your profile.\n` +
    `4. Partners can log in separately and view your order statuses only.\n` +
    `5. You can remove a partner at any time using the "Remove Partner" button.`
  );
};

  return (
    <main>
      <section className="ps-containers">
        <div className="ps-grid ps-grid-2">
          {/* Add Partner Form */}
          <div className="ps-card partner-card-ps" role="form" aria-labelledby="add-partner-title">
            <div className="ps-card-header">
              <h2 id="add-partner-title" className="ps-title">
                Add a Partner
                <FaInfoCircle style={{ cursor: 'pointer', fontSize: '1.2rem', color: '#EA8716',marginLeft:'10px' }} onClick={showPartnerInfo}/>
              </h2>
              <p className="ps-subtle">Create a partner account linked to your profile. Press Enter to submit.</p>
            </div>
            <div className="ps-card-body">
              <form onSubmit={handleAddPartner} className="ps-stack" noValidate>
                <div className="ps-field">
                  <label htmlFor="name" className="ps-label">
                    Partner Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    className="ps-input"
                    placeholder=""
                    value={partnerData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="ps-field">
                  <label htmlFor="email" className="ps-label">
                    Partner Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    name="email"
                    className="ps-input"
                    placeholder="partner@example.com"
                    value={partnerData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="ps-field">
                  <label htmlFor="password" className="ps-label">
                    Partner Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    name="password"
                    className="ps-input"
                    placeholder=""
                    value={partnerData.password}
                    onChange={handleChange}
                    required
                  />
                </div>
                {message && <p className={message.startsWith("Error") ? "ps-error" : "ps-success"}>{message}</p>}
                <div className="ps-actions" style={{ justifyContent: "flex-end" }}>
                  <button type="submit" className="ps-btn ps-btn-primary" disabled={submitting}>
                    {submitting ? "Adding..." : "Add Partner"}
                  </button>
                </div>
              </form>
            </div>
            <div className="ps-card-footer">
              Partners inherit key structure fields (Company, GSTIN, PAN) from your profile.
            </div>
          </div>

          {/* Partners List */}
          <div className="ps-card partner-card-ps" role="region" aria-labelledby="partners-title">
            <div className="ps-card-header">
              <h2 id="partners-title" className="ps-title">Your Partners</h2>
              <p className="ps-subtle">All partners linked to your profile.</p>
            </div>
            <div className="ps-card-body ps-stack">
              {partners.length > 0 ? (
                partners.map((partner) => (
                  <dl key={partner.uid} className="ps-stack partner-item">
                    <div className="ps-grid" style={{ gridTemplateColumns: "120px 1fr", gap: "0.5rem" }}>
                      <dt className="ps-subtle">Name</dt>
                      <dd>{partner.name || "—"}</dd>
                    </div>
                    <div className="ps-grid" style={{ gridTemplateColumns: "120px 1fr", gap: "0.5rem" }}>
                      <dt className="ps-subtle">Email</dt>
                      <dd>{partner.email || "—"}</dd>
                    </div>
                    <div className="ps-grid" style={{ gridTemplateColumns: "120px 1fr", gap: "0.5rem" }}>
                      <dt className="ps-subtle">Created At</dt>
                      <dd>{partner.createdAt ? new Date(partner.createdAt).toLocaleString() : "—"}</dd>
                    </div>
                    <div className="ps-actions" style={{ justifyContent: "flex-end", marginTop: "0.5rem" }}>
                      <button
                        className="ps-btn ps-btn-danger"
                        onClick={() => setPartnerToRemove(partner.uid)}
                      >
                        Remove Partner
                      </button>
                      {partnerToRemove && (
                        <div className="confirm-modal">
                          <div className="confirm-content">
                            <p>Are you sure you want to remove this partner?</p>
                            <div className="ps-actions">
                              <button
                                className="ps-btn ps-btn-danger"
                                onClick={() => {
                                  handleRemovePartner(partnerToRemove)
                                  setPartnerToRemove(null)
                                }}
                              >
                                Yes, Remove
                              </button>
                              <button
                                className="ps-btn ps-btn-secondary"
                                onClick={() => setPartnerToRemove(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                    </div>
                  </dl>
                ))
              ) : (
                <p className="ps-subtle">No partners added yet.</p>
              )}
            </div>
          </div>
        </div>
      </section>

      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} profile={mainProfile} title="Main Profile" />
    </main>
  )
}
